import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control } from 'react-hook-form';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  TextField,
} from '@mui/material';

import { CanadaProvinces } from '#apps/client-web/features/audit-form-background/constants';
import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';

export type AddressFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const AddressFormDialog: FC<
  PropsWithChildren<AddressFormDialogProps>
> = ({ open, control, onClose }) => (
  <SubFormDialog title="Address" open={open} onClose={onClose}>
    <Stack spacing={2}>
      <Controller
        control={control}
        name="general.address.addressLine1"
        rules={{
          maxLength: 120,
        }}
        render={({ field, fieldState }) => (
          <FormControl fullWidth {...getFormErrorAttributes(fieldState.error)}>
            <TextField
              {...field}
              label="Street Address"
              type="text"
              variant="outlined"
              {...getFormErrorAttributes(fieldState.error)}
            />
            <FormHelperText>{fieldState.error?.message}</FormHelperText>
          </FormControl>
        )}
      />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            control={control}
            name="general.address.city"
            rules={{
              maxLength: 50,
            }}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <TextField
                  {...field}
                  label="City/Town/Municipality"
                  type="text"
                  variant="outlined"
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            control={control}
            name="general.address.state"
            rules={{
              maxLength: 50,
            }}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <Autocomplete
                  {...field}
                  {...getFormErrorAttributes(fieldState.error)}
                  options={CanadaProvinces}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Province"
                      variant="outlined"
                      {...getFormErrorAttributes(fieldState.error)}
                    />
                  )}
                  value={field.value ?? null}
                  onChange={(_, value) => field.onChange(value)}
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            control={control}
            name="general.address.postalCode"
            rules={{
              maxLength: 50,
            }}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <TextField
                  {...field}
                  label="Postal Code"
                  type="text"
                  variant="outlined"
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
    </Stack>
  </SubFormDialog>
);
